import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip } from '@mui/material';
import { SchoolYear } from '../types';
import { storageManager } from '../utils/storage';
import { pageStateManager } from '../utils/pageStateManager';

type ContextSelectorProps = {
  onContextChange?: (schoolYearId: string, termId: string) => void;
};

export default function ContextSelector({ onContextChange }: ContextSelectorProps) {
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [currentContext, setCurrentContext] = useState<{
    schoolYearId: string | null;
    termId: string | null;
  }>({ schoolYearId: null, termId: null });

  // 状态恢复标记
  const hasRestoredState = React.useRef(false);

  useEffect(() => {
    // 加载学年数据
    loadData();
    
    // 添加上下文变更监听器
    const handleContextChange = () => {
      console.log("ContextSelector: Context changed, reloading data...");
      loadData();
    };
    
    window.addEventListener('contextChange', handleContextChange);
    
    // 组件卸载时移除监听器
    return () => {
      window.removeEventListener('contextChange', handleContextChange);
    };
  }, []);

  // 状态恢复useEffect - 等待学年数据加载完成后恢复状态
  useEffect(() => {
    if (schoolYears.length > 0 && !hasRestoredState.current) {
      hasRestoredState.current = true;
      
      // 尝试恢复之前保存的学年学期选择
      const savedSchoolYearId = pageStateManager.getSelectedClass('global_context_schoolYear');
      const savedTermId = pageStateManager.getSelectedClass('global_context_term');
      
      console.log(`🔄 全局上下文：恢复学年学期选择状态: 学年=${savedSchoolYearId}, 学期=${savedTermId}`);
      
      // 验证保存的学年和学期是否仍然有效
      const validSchoolYear = schoolYears.find(y => y.id === savedSchoolYearId);
      const validTerm = validSchoolYear?.terms?.find(t => t.id === savedTermId);
      
      if (validSchoolYear && validTerm) {
        // 如果当前上下文为空且保存的状态有效，则恢复状态
        if (!currentContext.schoolYearId && !currentContext.termId) {
          console.log(`📖 全局上下文：恢复到保存的状态 - 学年: ${validSchoolYear.name}, 学期: ${validTerm.name}`);
          storageManager.setCurrentContext(savedSchoolYearId, savedTermId);
          setCurrentContext({ schoolYearId: savedSchoolYearId, termId: savedTermId });
          
          // 触发上下文变更事件
          if (onContextChange) {
            onContextChange(savedSchoolYearId, savedTermId);
          }
          
          const event = new CustomEvent('contextChange', { 
            detail: { schoolYearId: savedSchoolYearId, termId: savedTermId } 
          });
          window.dispatchEvent(event);
        }
      } else {
        console.warn(`全局上下文：保存的学年学期状态无效，使用当前上下文`);
      }
    }
  }, [schoolYears, currentContext, onContextChange]);

  const loadData = () => {
    try {
      const years = storageManager.getSchoolYears();
      // 确保 years 是数组
      const yearsArray = Array.isArray(years) ? years : [];
      console.log("[ContextSelector] 加载的学年数据:", yearsArray);
      setSchoolYears(yearsArray);
      
      // 获取当前上下文
      const context = storageManager.getCurrentContext();
      setCurrentContext(context);
    } catch (error) {
      console.error("[ContextSelector] 加载数据时出错:", error);
      setSchoolYears([]);
    }
  };

  const handleSchoolYearChange = (schoolYearId: string) => {
    console.log(`[ContextSelector] 开始学年变更处理: 新学年ID=${schoolYearId}, 当前上下文=${JSON.stringify(currentContext)}`);
    
    const year = schoolYears.find(y => y.id === schoolYearId);
    // 如果没有选中学年，重置上下文
    if (!year) {
      console.log(`[ContextSelector] 未找到选中的学年，重置上下文`);
      setCurrentContext({ schoolYearId: null, termId: null });
      storageManager.setCurrentContext('', ''); // 重置存储器中的上下文
      
      // 清除保存的状态
      pageStateManager.saveSelectedClass('global_context_schoolYear', '');
      pageStateManager.saveSelectedClass('global_context_term', '');
      return;
    }
    
    // 默认选择该学年的第一个学期
    const termId = year.terms && year.terms.length > 0 ? year.terms[0].id : '';
    
    console.log(`[ContextSelector] 学年变更: 学年ID=${schoolYearId}, 第一个学期ID=${termId}, 学年名称=${year.name}`);
    console.log(`[ContextSelector] 该学年的学期:`, year.terms);
    
    // 更新上下文
    storageManager.setCurrentContext(schoolYearId, termId);
    
    // 保存学年学期选择状态
    pageStateManager.saveSelectedClass('global_context_schoolYear', schoolYearId);
    pageStateManager.saveSelectedClass('global_context_term', termId);
    console.log(`💾 全局上下文：已保存学年学期选择状态: 学年=${schoolYearId}, 学期=${termId}`);
    
    // 更新组件状态
    setCurrentContext({ schoolYearId, termId });
    
    // 如果提供了回调，调用它
    if (onContextChange) {
      console.log(`[ContextSelector] 调用onContextChange回调`);
      onContextChange(schoolYearId, termId);
    } else {
      console.log(`[ContextSelector] 未提供onContextChange回调`);
    }
    
    // 手动触发自定义事件，确保所有组件都收到通知
    const event = new CustomEvent('contextChange', { 
      detail: { schoolYearId, termId } 
    });
    console.log(`[ContextSelector] 手动触发contextChange事件`);
    window.dispatchEvent(event);
  };

  const handleTermChange = (termId: string) => {
    console.log(`[ContextSelector] 开始学期变更处理: 新学期ID=${termId}, 当前上下文=${JSON.stringify(currentContext)}`);
    
    if (!currentContext.schoolYearId) {
      console.log(`[ContextSelector] 未选择学年，无法变更学期`);
      return;
    }
    
    // 获取当前学年和选择的学期信息
    const year = schoolYears.find(y => y.id === currentContext.schoolYearId);
    const term = year?.terms?.find(t => t.id === termId);
    
    console.log(`[ContextSelector] 学期变更: 学年ID=${currentContext.schoolYearId}, 学期ID=${termId}`);
    if (year && term) {
      console.log(`[ContextSelector] 学年="${year.name}", 学期="${term.name}"`);
    }
    
    // 更新上下文
    storageManager.setCurrentContext(currentContext.schoolYearId, termId);
    
    // 保存学期选择状态
    pageStateManager.saveSelectedClass('global_context_term', termId);
    console.log(`💾 全局上下文：已保存学期选择状态: ${termId}`);
    
    // 更新组件状态
    setCurrentContext({ ...currentContext, termId });
    
    // 如果提供了回调，调用它
    if (onContextChange) {
      console.log(`[ContextSelector] 调用onContextChange回调`);
      onContextChange(currentContext.schoolYearId, termId);
    } else {
      console.log(`[ContextSelector] 未提供onContextChange回调`);
    }
    
    // 手动触发自定义事件，确保所有组件都收到通知
    const event = new CustomEvent('contextChange', { 
      detail: { schoolYearId: currentContext.schoolYearId, termId } 
    });
    console.log(`[ContextSelector] 手动触发contextChange事件`);
    window.dispatchEvent(event);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      gap: { xs: 0.5, sm: 1 }
    }}>
      <Typography 
        variant="caption" 
        sx={{ 
          mr: { xs: 0.5, sm: 1 }, 
          whiteSpace: 'nowrap', 
          display: { xs: 'none', md: 'block' },
          fontSize: { sm: '0.75rem', md: '0.875rem' }
        }}
      >
        当前环境:
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 0.5, sm: 1 }
      }}>
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: { xs: 90, sm: 110, md: 120 }, 
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiSelect-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 0.5, sm: 1 }
            }
          }}
        >
          <InputLabel id="school-year-select-label">学年</InputLabel>
          <Select
            labelId="school-year-select-label"
            value={currentContext.schoolYearId || ''}
            label="学年"
            onChange={(e) => handleSchoolYearChange(e.target.value)}
            sx={{
              height: { xs: 32, sm: 40 }
            }}
          >
            {!Array.isArray(schoolYears) || schoolYears.length === 0 ? (
              <MenuItem value="" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <em>请先创建学年</em>
              </MenuItem>
            ) : (
              schoolYears.map((year) => (
                <MenuItem 
                  key={year.id} 
                  value={year.id}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {year.name}
                  {year.isActive && (
                    <Chip 
                      label="当前" 
                      color="success" 
                      size="small" 
                      sx={{ 
                        ml: 1, 
                        height: { xs: 16, sm: 20 },
                        fontSize: { xs: '0.6rem', sm: '0.75rem' }
                      }}
                    />
                  )}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl 
          size="small" 
          sx={{ 
            minWidth: { xs: 90, sm: 110, md: 120 },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiSelect-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 0.5, sm: 1 }
            }
          }}
          disabled={!currentContext.schoolYearId || !schoolYears.find(y => y.id === currentContext.schoolYearId)?.terms?.length}
        >
          <InputLabel id="term-select-label">学期</InputLabel>
          <Select
            labelId="term-select-label"
            value={currentContext.termId || ''}
            label="学期"
            onChange={(e) => handleTermChange(e.target.value)}
            sx={{
              height: { xs: 32, sm: 40 }
            }}
          >
            {!currentContext.schoolYearId ? (
              <MenuItem value="" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <em>请先选择学年</em>
              </MenuItem>
            ) : (
              Array.isArray(schoolYears) && schoolYears.length > 0 &&
              schoolYears
                .find(y => y.id === currentContext.schoolYearId)
                ?.terms?.map((term) => (
                  <MenuItem 
                    key={term.id} 
                    value={term.id}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {term.name}
                    {term.isActive && (
                      <Chip 
                        label="当前" 
                        color="success" 
                        size="small" 
                        sx={{ 
                          ml: 1, 
                          height: { xs: 16, sm: 20 },
                          fontSize: { xs: '0.6rem', sm: '0.75rem' }
                        }}
                      />
                    )}
                  </MenuItem>
                ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
} 